import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const REPORT_TYPES = [
  { value: 'search_topics', label: 'Top Searched Topics' },
  { value: 'issue_summary', label: 'Issue Summary' },
  { value: 'location_summary', label: 'Location Summary' },
  { value: 'monthly_summary', label: 'Monthly Summary' },
];

const TOPICS = [
  'overtime', 'discipline', 'grievance', 'safety', 'scheduling',
  'pay', 'benefits', 'contract', 'bereavement', 'vacation',
  'equipment', 'procedures', 'training', 'other'
];

const ISSUE_TYPES = [
  'overtime_bypass', 'safety_concern', 'discipline_issue', 'staffing_shortage',
  'scheduling_issue', 'pay_problem', 'contract_violation', 'equipment_problem',
  'harassment', 'management_issue', 'other'
];

function AdminAnalytics({ user }) {
  const navigate = useNavigate();
  const [reportType, setReportType] = useState('search_topics');
  const [startDate, setStartDate] = useState(getDefaultStartDate());
  const [endDate, setEndDate] = useState(getDefaultEndDate());
  const [topic, setTopic] = useState('');
  const [issueType, setIssueType] = useState('');
  const [location, setLocation] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [reportData, setReportData] = useState(null);

  function getDefaultStartDate() {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return d.toISOString().split('T')[0];
  }

  function getDefaultEndDate() {
    return new Date().toISOString().split('T')[0];
  }

  const buildParams = (format = 'json') => {
    const params = new URLSearchParams({
      report_type: reportType,
      start_date: startDate,
      end_date: endDate,
      format: format,
    });
    if (topic && reportType === 'search_topics') params.append('topic', topic);
    if (issueType && (reportType === 'issue_summary' || reportType === 'location_summary')) {
      params.append('issue_type', issueType);
    }
    if (location && (reportType === 'issue_summary' || reportType === 'location_summary')) {
      params.append('location', location);
    }
    return params;
  };

  const runReport = async () => {
    setLoading(true);
    setError('');
    setReportData(null);

    try {
      const token = localStorage.getItem('token');
      const params = buildParams('json');
      const response = await axios.get(
        `${API}/api/ask-stewie/analytics/report?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setReportData(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  const downloadCsv = () => {
    const token = localStorage.getItem('token');
    const params = buildParams('csv');
    // Open in new tab with auth header via form submission
    window.open(
      `${API}/api/ask-stewie/analytics/report?${params.toString()}`,
      '_blank'
    );
  };

  const renderTable = () => {
    if (!reportData || !reportData.rows || reportData.rows.length === 0) {
      return <p className="no-data">No data found for the selected criteria.</p>;
    }

    const columns = Object.keys(reportData.rows[0]);

    return (
      <table className="analytics-table" data-testid="analytics-results-table">
        <thead>
          <tr>
            {columns.map(col => (
              <th key={col}>{formatColumnName(col)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {reportData.rows.map((row, idx) => (
            <tr key={idx}>
              {columns.map(col => (
                <td key={col}>{row[col]}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  const formatColumnName = (name) => {
    return name
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());
  };

  if (!user?.is_admin) {
    return (
      <div className="admin-analytics-page">
        <div className="access-denied">
          <h2>Access Denied</h2>
          <p>Admin access required to view analytics reports.</p>
          <button onClick={() => navigate('/')}>Go to Dashboard</button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-analytics-page" data-testid="admin-analytics-page">
      <header className="analytics-header">
        <h1>Analytics Reports</h1>
        <button className="back-btn" onClick={() => navigate('/admin')}>
          ← Back to Admin
        </button>
      </header>

      <div className="analytics-controls">
        <div className="control-group">
          <label htmlFor="report-type">Report Type</label>
          <select
            id="report-type"
            value={reportType}
            onChange={(e) => setReportType(e.target.value)}
            data-testid="report-type-select"
          >
            {REPORT_TYPES.map(rt => (
              <option key={rt.value} value={rt.value}>{rt.label}</option>
            ))}
          </select>
        </div>

        <div className="control-group">
          <label htmlFor="start-date">Start Date</label>
          <input
            type="date"
            id="start-date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            data-testid="start-date-input"
          />
        </div>

        <div className="control-group">
          <label htmlFor="end-date">End Date</label>
          <input
            type="date"
            id="end-date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            data-testid="end-date-input"
          />
        </div>

        {reportType === 'search_topics' && (
          <div className="control-group">
            <label htmlFor="topic-filter">Filter by Topic</label>
            <select
              id="topic-filter"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              data-testid="topic-filter-select"
            >
              <option value="">All Topics</option>
              {TOPICS.map(t => (
                <option key={t} value={t}>{formatColumnName(t)}</option>
              ))}
            </select>
          </div>
        )}

        {(reportType === 'issue_summary' || reportType === 'location_summary') && (
          <>
            <div className="control-group">
              <label htmlFor="issue-type-filter">Filter by Issue Type</label>
              <select
                id="issue-type-filter"
                value={issueType}
                onChange={(e) => setIssueType(e.target.value)}
                data-testid="issue-type-filter-select"
              >
                <option value="">All Issue Types</option>
                {ISSUE_TYPES.map(it => (
                  <option key={it} value={it}>{formatColumnName(it)}</option>
                ))}
              </select>
            </div>

            <div className="control-group">
              <label htmlFor="location-filter">Filter by Location</label>
              <input
                type="text"
                id="location-filter"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="Enter location..."
                data-testid="location-filter-input"
              />
            </div>
          </>
        )}
      </div>

      <div className="analytics-actions">
        <button
          className="run-report-btn"
          onClick={runReport}
          disabled={loading}
          data-testid="run-report-btn"
        >
          {loading ? 'Running...' : 'Run Report'}
        </button>
        <button
          className="download-csv-btn"
          onClick={downloadCsv}
          disabled={loading}
          data-testid="download-csv-btn"
        >
          Download CSV
        </button>
      </div>

      {error && <div className="error-message" data-testid="error-message">{error}</div>}

      {reportData && (
        <div className="analytics-results" data-testid="analytics-results">
          <div className="results-header">
            <h2>{REPORT_TYPES.find(rt => rt.value === reportData.report_type)?.label}</h2>
            <span className="results-meta">
              {reportData.start_date} to {reportData.end_date} • {reportData.row_count} rows
            </span>
          </div>
          {renderTable()}
        </div>
      )}
    </div>
  );
}

export default AdminAnalytics;
